import axios from "axios";
import { notify } from "./util/helpers";
import { getRefreshTokenExpiry } from "./sessionManager";

const urlLookup = {
  adult: {
    development: "https://devapi.rosehealth.com",
    staging: "https://stagingapi.precisebh.com/",
    production: "https://api.precisebh.com/",
    prod: "https://api.precisebh.com/",
    preprod: "https://api.precisebh.com/",
  },
  youth: {
    development: "https://youthdevapi.rosehealth.com",
    staging: "https://youthstagingapi.rosehealth.com",
    production: "https://youthapi.rosehealth.com",
    prod: "https://youthapi.rosehealth.com/",
  },
};

export const BASE_URL =
  process.env.REACT_APP_API_URL ||
  (urlLookup[process.env.REACT_APP_DASHBOARD_TYPE] &&
    (urlLookup[process.env.REACT_APP_DASHBOARD_TYPE][process.env.REACT_APP_ENV] ||
     urlLookup[process.env.REACT_APP_DASHBOARD_TYPE]["production"])) ||
  "https://api.precisebh.com/";

// export const BASE_URL =
//   process.env.REACT_APP_API_URL ||
//   "https://preprodapi.precisebh.com/";

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    //* Additional Interceptor Logic for request goes here
    if (Object.values(config.params || {}).some((param) => param === undefined || param === null || Number.isNaN(param) || param === "NaN")) {
      // Log or handle the error as needed
      console.error('Undefined parameter found in the request. Request aborted.');
    }
    else
      return config;
  },
  (error) => {
    console.log("error: " + error);
    return Promise.reject(error);
  }
);

// ── Token Refresh State ──────────────────────────────────────
// Queue pattern: if multiple API calls get 401 at the same time,
// only one refresh request is made. The others wait in the queue
// and are retried with the new token once the refresh completes.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const redirectToLogin = (reason = "session_expired") => {
  if (window.store) {
    window.store.dispatch({ type: "app/logoutUser" });
  }
  if (window.persistor) {
    window.persistor.purge();
  }
  localStorage.clear();
  if (sessionStorage.getItem("manual_logout") === "true") {
    window.location.href = "/login";
  } else {
    window.location.href = `/login?reason=${reason}`;
  }
};

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.headers && response.headers.date) {
      const serverTime = new Date(response.headers.date).getTime();
      const clientTime = Date.now();
      const skew = serverTime - clientTime;
      localStorage.setItem("server_clock_skew", skew.toString());
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors that haven't been retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip token refresh for auth endpoints (login, OTP, refresh itself)
      // to prevent infinite loops
      if (originalRequest.url?.includes("/auth/")) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refresh");

      // No refresh token available → force logout
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers["Authorization"] = `JWT ${newToken}`;
          return axiosInstance(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      isRefreshing = true;

      try {
        // Use raw axios (not axiosInstance) to avoid interceptor recursion
        const { data } = await axios.post(
          `${BASE_URL.replace(/\/$/, "")}/auth/token/refresh/`,
          { refresh: refreshToken }
        );

        localStorage.setItem("access", data.access);
        if (data.refresh) {
          localStorage.setItem("refresh", data.refresh);
        }

        // Retry all queued requests with the new token
        processQueue(null, data.access);

        originalRequest.headers["Authorization"] = `JWT ${data.access}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        // Refresh token expired or invalid → force logout
        processQueue(refreshError, null);
        
        // Determine the reason for redirection
        const expiry = getRefreshTokenExpiry();
        const reason = (expiry && Date.now() >= expiry) ? "absolute_timeout" : "session_expired";
        
        redirectToLogin(reason);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other error codes (403, 406, 429) as before
    if (error.response) {
      if (error.response.status === 406) {
        notify(
          error.response?.data?.detail || "Something went wrong!",
          "info"
        );
      } else if (error.response.status === 403) {
        notify(
          error.response?.data?.detail || "Something went wrong!",
          "info"
        );
      } else if (error.response.status === 429) {
        notify(
          error.response?.data?.detail || "Too Many Incorrect Attempt",
          "info"
        );
      }
    }
    return Promise.reject(error);
  }
);

// export const CMS_BASE_URL = "https://cms.rosehealth.com";
export const CMS_BASE_URL = "/api/cms";

export default axiosInstance;

